import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowClockwise,
  CaretLeft,
  CaretRight,
  Check,
  DownloadSimple,
  ImageSquare,
  MagicWand,
  ShareNetwork,
  Sparkle,
  Trash,
  Upload,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

import { DashboardLayout } from '../../components/Layout';
import AIPremiumButton from '../../components/AIPremiumGate';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { resolveMediaUrl } from '../../utils/media';

const GENERATION_STAGES = [
  {
    label: 'Creating the artwork',
    detail: 'The AI model is composing your photos and celebration details',
  },
  {
    label: 'Preserving your people',
    detail: 'Keeping recognizable faces and every selected person in the design',
  },
  {
    label: 'Building the visual world',
    detail: 'Creating a cohesive setting, palette and celebration style',
  },
  {
    label: 'Rendering the details',
    detail: 'Placing the exact names, date and event information into the artwork',
  },
  {
    label: 'Finishing your image',
    detail: 'Preparing one high-resolution, share-ready design',
  },
];

const socialStudioJobs = new Map();

const startSocialStudioJob = (eventId, kind, requestFactory, metadata = {}) => {
  const existing = socialStudioJobs.get(eventId);
  if (existing?.status === 'pending') return existing;

  const job = {
    eventId,
    kind,
    metadata,
    startedAt: Date.now(),
    status: 'pending',
    response: null,
    error: null,
    promise: null,
  };
  job.promise = requestFactory()
    .then((response) => {
      job.status = 'success';
      job.response = response;
      return job;
    })
    .catch((error) => {
      job.status = 'error';
      job.error = error;
      return job;
    });
  socialStudioJobs.set(eventId, job);
  return job;
};

const mergeDesigns = (current, incoming) => {
  const incomingIds = new Set(incoming.map((item) => item.id));
  return [...incoming, ...current.filter((item) => !incomingIds.has(item.id))];
};

const detailFromError = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg).filter(Boolean).join(', ') || fallback;
  return fallback;
};

const svgToPngBlob = (svg, width, height) => new Promise((resolve, reject) => {
  const source = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const sourceUrl = URL.createObjectURL(source);
  const image = new Image();
  image.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    context.drawImage(image, 0, 0, width, height);
    URL.revokeObjectURL(sourceUrl);
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('The browser could not create the PNG'));
    }, 'image/png', 1);
  };
  image.onerror = () => {
    URL.revokeObjectURL(sourceUrl);
    reject(new Error('The generated artwork could not be loaded'));
  };
  image.src = sourceUrl;
});

const DesignCanvas = ({ design, className = '', maximumHeight }) => {
  const aspectRatio = design.width / design.height;
  const commonProps = {
    className: `min-w-0 max-w-full overflow-hidden bg-muted shadow-sm [&>svg]:block [&>svg]:h-full [&>svg]:w-full ${className}`,
    style: {
      aspectRatio: `${design.width} / ${design.height}`,
      ...(maximumHeight ? { width: `min(100%, ${maximumHeight * aspectRatio}px)` } : {}),
    },
  };
  if (design.svg) {
    return <div {...commonProps} dangerouslySetInnerHTML={{ __html: design.svg }} />;
  }
  return (
    <div {...commonProps}>
      <img
        src={resolveMediaUrl(design.url)}
        alt={`${design.occasion.replaceAll('_', ' ')} design`}
        draggable={false}
        className="h-full w-full object-contain"
      />
    </div>
  );
};

const PreviewGenerationLoader = ({ stage, isVariation }) => {
  const message = GENERATION_STAGES[stage % GENERATION_STAGES.length];
  return (
    <div
      className="relative flex min-h-[300px] w-full items-center justify-center overflow-hidden rounded-lg border border-wedding-gold/20 bg-gradient-to-br from-wedding-gold/10 via-background to-wedding-sage/15 px-4 sm:min-h-[420px] sm:px-6 xl:min-h-[488px]"
      role="status"
      aria-live="polite"
    >
      <div className="absolute -left-12 top-10 h-36 w-36 animate-pulse rounded-full bg-wedding-gold/15 blur-3xl" />
      <div className="absolute -right-10 bottom-8 h-44 w-44 animate-pulse rounded-full bg-wedding-sage/20 blur-3xl" />
      <Sparkle className="absolute left-[16%] top-[18%] animate-pulse text-wedding-gold" size={22} weight="fill" />
      <Sparkle className="absolute bottom-[22%] right-[15%] animate-bounce text-wedding-sage" size={18} weight="fill" />

      <div className="relative z-10 max-w-sm text-center">
        <div className="relative mx-auto mb-5 h-24 w-32 sm:mb-7 sm:h-28 sm:w-36">
          <div className="absolute left-1 top-3 h-24 w-16 -rotate-12 animate-pulse rounded-lg border border-wedding-gold/30 bg-background/80 shadow-lg" />
          <div className="absolute right-1 top-3 h-24 w-16 rotate-12 animate-pulse rounded-lg border border-wedding-sage/40 bg-background/80 shadow-lg" />
          <div className="absolute left-1/2 top-0 flex h-28 w-20 -translate-x-1/2 items-center justify-center rounded-xl border border-wedding-gold/40 bg-background shadow-xl">
            <MagicWand className="animate-bounce text-wedding-gold" size={30} weight="duotone" />
          </div>
        </div>

        <p className="text-xs font-bold uppercase tracking-[0.24em] text-wedding-gold">
          {isVariation ? 'Remixing your design' : 'GalaGo art studio'}
        </p>
        <p key={message.label} className="mt-3 text-lg font-semibold text-wedding-charcoal sm:text-xl">
          {message.label}<span className="animate-pulse">...</span>
        </p>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{message.detail}</p>

        <div className="mt-4 flex justify-center gap-2 sm:mt-6" aria-hidden="true">
          {GENERATION_STAGES.map((item, index) => (
            <span
              key={item.label}
              className={`h-1.5 rounded-full transition-all duration-500 ${index === stage ? 'w-7 bg-wedding-gold' : 'w-1.5 bg-wedding-sage/40'}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const PreviewGuidance = ({ message, onRetry, retryDisabled, retryLabel }) => (
  <div
    className="relative w-full overflow-hidden rounded-xl border border-wedding-gold/30 bg-gradient-to-br from-wedding-gold/10 via-background to-wedding-sage/10 p-7 text-center"
    role="alert"
  >
    <Sparkle className="absolute right-6 top-6 text-wedding-gold/40" size={22} weight="fill" />
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-wedding-gold/15 text-wedding-gold">
      <MagicWand size={28} weight="duotone" />
    </div>
    <p className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-wedding-gold">
      Let&apos;s adjust the canvas
    </p>
    <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-wedding-charcoal">{message}</p>
    <AIPremiumButton
      featureName="Social Studio design generation"
      className="mt-6"
      variant="outline"
      onClick={onRetry}
      disabled={retryDisabled}
    >
      <ArrowClockwise size={17} className="mr-2" /> {retryLabel}
    </AIPremiumButton>
  </div>
);

export default function SocialContentStudio() {
  const { currentEvent } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [context, setContext] = useState(null);
  const [occasion, setOccasion] = useState('save_the_date');
  const [coupleImage, setCoupleImage] = useState(null);
  const [additionalPrompt, setAdditionalPrompt] = useState('');
  const [inspirationImage, setInspirationImage] = useState(null);
  const [visibility, setVisibility] = useState({});
  const [caption, setCaption] = useState('');
  const [designs, setDesigns] = useState([]);
  const [selectedDesignId, setSelectedDesignId] = useState('');
  const [variationInstruction, setVariationInstruction] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [generationError, setGenerationError] = useState('');
  const [retryAction, setRetryAction] = useState('generate');
  const [generating, setGenerating] = useState(false);
  const [varying, setVarying] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingInspiration, setUploadingInspiration] = useState(false);
  const [generationStage, setGenerationStage] = useState(0);
  const [removingDesignId, setRemovingDesignId] = useState('');
  const [jobVersion, setJobVersion] = useState(0);
  const activeEventIdRef = useRef(currentEvent?.id || '');
  const handledJobRef = useRef(null);
  const swipeStartXRef = useRef(null);

  useEffect(() => {
    if (!currentEvent?.id) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    activeEventIdRef.current = currentEvent.id;
    setLoading(true);
    setLoadError('');
    setGenerationError('');
    setDesigns([]);
    setSelectedDesignId('');
    setCaption('');
    Promise.all([
      api.get(`/events/${currentEvent.id}/social-content/catalog`),
      api.get(`/events/${currentEvent.id}/social-content/context`),
      api.get(`/events/${currentEvent.id}/social-content/assets`),
    ])
      .then(([catalogResponse, contextResponse, assetsResponse]) => {
        if (!active) return;
        const nextCatalog = catalogResponse.data;
        const nextContext = contextResponse.data;
        const nextDesigns = assetsResponse.data;
        setCatalog(nextCatalog);
        setContext(nextContext);
        setVisibility(nextCatalog.visibility_defaults);
        setOccasion(nextCatalog.occasions[0].id);
        setCoupleImage(null);
        setAdditionalPrompt('');
        setInspirationImage(null);
        setDesigns((current) => mergeDesigns(current, nextDesigns));
        setSelectedDesignId((current) => current || nextDesigns[0]?.id || '');
        setCaption((current) => current || nextDesigns[0]?.caption || '');
      })
      .catch((error) => {
        if (!active) return;
        setLoadError(detailFromError(error, 'Social Studio could not load this event'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      if (activeEventIdRef.current === currentEvent.id) activeEventIdRef.current = '';
    };
  }, [currentEvent?.id]);

  useEffect(() => {
    const eventId = currentEvent?.id;
    if (!eventId) return undefined;
    activeEventIdRef.current = eventId;
    const job = socialStudioJobs.get(eventId);
    if (!job || handledJobRef.current === job) return undefined;

    handledJobRef.current = job;
    let attached = true;
    setGenerationStage(
      Math.floor((Date.now() - job.startedAt) / 1800) % GENERATION_STAGES.length,
    );
    setGenerating(job.kind === 'generate');
    setVarying(job.kind === 'variation');

    job.promise.then((completedJob) => {
      if (!attached || activeEventIdRef.current !== eventId) return;
      if (completedJob.status === 'success') {
        const incoming = completedJob.kind === 'generate'
          ? completedJob.response.data.designs
          : [completedJob.response.data];
        setDesigns((current) => mergeDesigns(current, incoming));
        setSelectedDesignId(incoming[0].id);
        setCaption(incoming[0].caption);
        setGenerationError('');
        if (completedJob.kind === 'variation') {
          setVariationInstruction('');
          toast.success('AI design refined');
        } else {
          toast.success('Fresh AI design created');
        }
      } else {
        const fallback = completedJob.kind === 'variation'
          ? 'Social Studio could not create the variation'
          : 'Social Studio could not generate the designs';
        const message = detailFromError(completedJob.error, fallback);
        setGenerationError(message);
        setRetryAction(completedJob.kind);
        if (completedJob.metadata.variationInstruction) {
          setVariationInstruction(completedJob.metadata.variationInstruction);
        }
        toast.error(message);
      }
      setGenerating(false);
      setVarying(false);
      if (socialStudioJobs.get(eventId) === completedJob) {
        socialStudioJobs.delete(eventId);
      }
    });

    return () => {
      attached = false;
      if (handledJobRef.current === job) handledJobRef.current = null;
    };
  }, [currentEvent?.id, jobVersion]);

  useEffect(() => {
    if (!generating && !varying) {
      setGenerationStage(0);
      return undefined;
    }
    const timer = window.setInterval(() => {
      setGenerationStage((current) => (current + 1) % GENERATION_STAGES.length);
    }, 1800);
    return () => window.clearInterval(timer);
  }, [generating, varying]);

  const photoSelectionIsValid = Boolean(coupleImage?.id);
  const selectedDesign = useMemo(
    () => designs.find((item) => item.id === selectedDesignId) || designs[0] || null,
    [designs, selectedDesignId],
  );
  const selectedDesignIndex = useMemo(
    () => designs.findIndex((item) => item.id === selectedDesign?.id),
    [designs, selectedDesign?.id],
  );

  const selectDesign = (design) => {
    if (!design) return;
    setSelectedDesignId(design.id);
    setCaption(design.caption);
  };

  const selectAdjacentDesign = (direction) => {
    if (designs.length < 2) return;
    const currentIndex = selectedDesignIndex >= 0 ? selectedDesignIndex : 0;
    const nextIndex = (currentIndex + direction + designs.length) % designs.length;
    selectDesign(designs[nextIndex]);
  };

  const handlePreviewKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      selectAdjacentDesign(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      selectAdjacentDesign(1);
    }
  };

  const handlePreviewTouchEnd = (event) => {
    if (swipeStartXRef.current === null) return;
    const endX = event.changedTouches[0]?.clientX;
    const distance = typeof endX === 'number' ? endX - swipeStartXRef.current : 0;
    swipeStartXRef.current = null;
    if (Math.abs(distance) < 45) return;
    selectAdjacentDesign(distance < 0 ? 1 : -1);
  };

  const resetResults = () => {
    setGenerationError('');
    setRetryAction('generate');
    setVariationInstruction('');
  };

  const uploadCoupleImage = async (file) => {
    if (!file || !currentEvent?.id) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    try {
      const response = await api.post(
        `/events/${currentEvent.id}/social-content/couple-image`,
        formData,
      );
      const uploaded = response.data;
      setCoupleImage(uploaded);
      resetResults();
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error(detailFromError(error, 'The photo could not be uploaded'));
    } finally {
      setUploading(false);
    }
  };

  const uploadInspirationImage = async (file) => {
    if (!file || !currentEvent?.id) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingInspiration(true);
    try {
      const response = await api.post(
        `/events/${currentEvent.id}/social-content/inspiration-image`,
        formData,
      );
      setInspirationImage(response.data);
      resetResults();
      toast.success('Inspiration reference added');
    } catch (error) {
      toast.error(detailFromError(error, 'The inspiration image could not be uploaded'));
    } finally {
      setUploadingInspiration(false);
    }
  };

  const generate = () => {
    if (!currentEvent?.id) return;
    setGenerationStage(0);
    setGenerating(true);
    setGenerationError('');
    setRetryAction('generate');
    handledJobRef.current = null;
    startSocialStudioJob(
      currentEvent.id,
      'generate',
      () => api.post(`/events/${currentEvent.id}/social-content/generate`, {
        occasion,
        couple_image_id: coupleImage.id,
        inspiration_image_id: inspirationImage?.id || null,
        additional_prompt: additionalPrompt,
        visibility,
      }),
    );
    setJobVersion((current) => current + 1);
  };

  const createVariation = () => {
    if (!selectedDesign || !variationInstruction.trim()) return;
    const instruction = variationInstruction.trim();
    setGenerationStage(0);
    setVarying(true);
    setGenerationError('');
    setRetryAction('variation');
    handledJobRef.current = null;
    startSocialStudioJob(
      currentEvent.id,
      'variation',
      () => api.post(
        `/events/${currentEvent.id}/social-content/assets/${selectedDesign.id}/variation`,
        { instruction, caption },
      ),
      { variationInstruction: instruction },
    );
    setJobVersion((current) => current + 1);
  };

  const getHydratedDesign = async (design) => {
    if (design.svg) return design;
    const response = await api.get(
      `/events/${currentEvent.id}/social-content/assets/${design.id}`,
    );
    const hydrated = response.data;
    setDesigns((current) => current.map(
      (item) => (item.id === hydrated.id ? hydrated : item),
    ));
    return hydrated;
  };

  const getPngFile = async () => {
    if (!selectedDesign) throw new Error('Select a generated design first');
    const hydrated = await getHydratedDesign(selectedDesign);
    let blob;
    if (hydrated.svg) {
      blob = await svgToPngBlob(hydrated.svg, hydrated.width, hydrated.height);
    } else {
      const response = await fetch(resolveMediaUrl(hydrated.url, { preferProxy: true }));
      if (!response.ok) throw new Error('The generated artwork could not be downloaded');
      blob = await response.blob();
    }
    return new File(
      [blob],
      `galago-${hydrated.occasion}-${hydrated.id}.png`,
      { type: 'image/png' },
    );
  };

  const removeDesign = async () => {
    if (!selectedDesign || !currentEvent?.id) return;
    if (!window.confirm('Remove this generated design from the event? This cannot be undone.')) return;
    setRemovingDesignId(selectedDesign.id);
    try {
      await api.delete(
        `/events/${currentEvent.id}/social-content/assets/${selectedDesign.id}`,
      );
      const remaining = designs.filter((item) => item.id !== selectedDesign.id);
      setDesigns(remaining);
      setSelectedDesignId(remaining[0]?.id || '');
      setCaption(remaining[0]?.caption || '');
      setGenerationError('');
      toast.success('Design removed from this event');
    } catch (error) {
      toast.error(detailFromError(error, 'The design could not be removed'));
    } finally {
      setRemovingDesignId('');
    }
  };

  const download = async () => {
    try {
      const file = await getPngFile();
      const url = URL.createObjectURL(file);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = file.name;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const share = async () => {
    try {
      const file = await getPngFile();
      if (!navigator.share || !navigator.canShare?.({ files: [file] })) {
        await download();
        toast.info('Device sharing is unavailable, so the PNG was downloaded');
        return;
      }
      await navigator.share({ files: [file], text: caption, title: context.event.name });
    } catch (error) {
      if (error.name !== 'AbortError') toast.error(error.message || 'The design could not be shared');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="py-20 text-center text-muted-foreground">Opening Social Studio…</div>
      </DashboardLayout>
    );
  }

  if (!currentEvent) {
    return (
      <DashboardLayout>
        <Card className="mx-auto max-w-xl">
          <CardContent className="p-8 text-center">Select an event to open Social Studio.</CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  if (loadError || !catalog || !context) {
    return (
      <DashboardLayout>
        <Card className="mx-auto max-w-xl border-destructive/40">
          <CardContent className="p-8 text-center text-destructive">{loadError || 'Social Studio data is unavailable'}</CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mx-auto w-full min-w-0 max-w-7xl overflow-x-hidden space-y-4 sm:space-y-5" data-testid="social-content-studio">
        <div className="flex flex-col gap-2 sm:gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-wedding-gold sm:text-sm">
              <Sparkle size={16} weight="fill" /> AI image generation
            </div>
            <h1 className="font-serif text-2xl font-bold text-wedding-charcoal sm:text-3xl">Social Studio</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground sm:text-base">
              Add a couple photo, optional inspiration and a prompt. GalaGo will create one ready-to-share design.
            </p>
          </div>
          <Badge variant="outline">Instagram Story</Badge>
        </div>

        {!catalog.ai_ready && (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            Social Studio generation requires OPENAI_API_KEY on the backend.
          </div>
        )}

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_460px] xl:gap-5">
          <div className="min-w-0">
            <Card className="min-w-0 overflow-hidden">
              <CardHeader className="p-4 pb-3">
                <CardTitle className="text-base sm:text-lg">Create your design</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 p-4 pt-0">
                <div className="grid grid-cols-2 gap-2">
                  {catalog.occasions.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => { setOccasion(item.id); resetResults(); }}
                      className={`rounded-xl border p-3 text-left transition ${occasion === item.id ? 'border-wedding-gold bg-wedding-gold/10' : 'hover:border-wedding-sage'}`}
                    >
                      <span className="flex items-center justify-between gap-2 text-sm font-semibold">
                        {item.name}
                        {occasion === item.id && <Check size={17} className="shrink-0 text-wedding-gold" weight="bold" />}
                      </span>
                    </button>
                  ))}
                </div>

                <section className="space-y-3">
                  <div>
                    <Label className="text-sm font-semibold">Photos</Label>
                    <p className="text-xs text-muted-foreground">Upload the people to preserve and, optionally, a visual reference for the design.</p>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-3 rounded-xl border p-3">
                  <div>
                    <Label className="text-sm font-semibold">Couple photo</Label>
                    <p className="text-xs text-muted-foreground">Required. The AI will keep the faces and people from this photo intact.</p>
                  </div>
                  {coupleImage ? (
                    <div className="flex items-center gap-3 rounded-xl border border-wedding-gold/60 bg-wedding-gold/5 p-3">
                      <img
                        src={resolveMediaUrl(coupleImage.url)}
                        alt={coupleImage.label || 'Uploaded couple photo'}
                        className="h-24 w-20 shrink-0 rounded-lg object-cover"
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">{coupleImage.label || 'Uploaded photo'}</p>
                        <p className="mt-1 text-xs text-muted-foreground">This photo will be used for generation</p>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">
                      Add a clear couple photo to begin.
                    </div>
                  )}
                  <Button asChild variant="outline" disabled={uploading}>
                    <label className="cursor-pointer">
                      <Upload size={17} className="mr-2" /> {uploading ? 'Uploading…' : coupleImage ? 'Replace photo' : 'Upload photo'}
                      <input
                        className="sr-only"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        disabled={uploading}
                        onChange={(event) => {
                          uploadCoupleImage(event.target.files?.[0]);
                          event.target.value = '';
                        }}
                      />
                    </label>
                  </Button>
                    </div>

                    <div className="space-y-3 rounded-xl border p-3">
                  <div>
                    <Label className="text-sm font-semibold">Inspiration photo <span className="font-normal text-muted-foreground">(optional)</span></Label>
                    <p className="text-xs text-muted-foreground">The AI will use this as design inspiration, not as a source for people or text.</p>
                  </div>
                  <div className="flex items-center gap-3 rounded-xl border p-3">
                    {inspirationImage ? (
                      <img src={resolveMediaUrl(inspirationImage.url)} alt="Design inspiration" className="h-20 w-16 shrink-0 rounded-lg object-cover" />
                    ) : (
                      <div className="flex h-20 w-16 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground"><ImageSquare size={24} /></div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{inspirationImage?.filename || 'No inspiration added'}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Button asChild type="button" size="sm" variant="outline" disabled={uploadingInspiration}>
                          <label htmlFor="social-inspiration-image" className="cursor-pointer">
                            <Upload size={15} className="mr-1.5" />
                            {uploadingInspiration ? 'Uploading…' : inspirationImage ? 'Replace' : 'Upload inspiration'}
                          </label>
                        </Button>
                        {inspirationImage && (
                          <Button type="button" size="sm" variant="ghost" onClick={() => { setInspirationImage(null); resetResults(); }}>Remove</Button>
                        )}
                      </div>
                    </div>
                    <input
                      id="social-inspiration-image"
                      className="sr-only"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      disabled={uploadingInspiration}
                      onChange={(event) => {
                        uploadInspirationImage(event.target.files?.[0]);
                        event.target.value = '';
                      }}
                    />
                  </div>
                    </div>
                  </div>
                </section>

                <section className="space-y-2 border-t pt-5">
                  <Label htmlFor="social-additional-prompt" className="text-sm font-semibold">Additional prompt <span className="font-normal text-muted-foreground">(optional)</span></Label>
                  <Textarea
                    id="social-additional-prompt"
                    value={additionalPrompt}
                    maxLength={500}
                    rows={3}
                    placeholder="For example: understated ivory florals, elegant editorial typography, warm evening light"
                    onChange={(event) => { setAdditionalPrompt(event.target.value); resetResults(); }}
                  />
                </section>

                <section className="space-y-3 border-t pt-5">
                  <div>
                    <Label className="text-sm font-semibold">Details included in the design</Label>
                    <p className="text-xs text-muted-foreground">These values come from your event. Turn off anything you do not want shown.</p>
                  </div>
                  {context.generation_details?.length ? (
                    <div className="grid gap-2 sm:grid-cols-2">
                      {context.generation_details.map((detail) => (
                        <div key={detail.key} className="flex min-w-0 items-center justify-between gap-3 rounded-xl border px-3 py-2.5">
                          <Label htmlFor={`social-${detail.key}`} className="min-w-0 cursor-pointer">
                            <span className="block text-xs text-muted-foreground">{detail.label}</span>
                            <span className="block truncate text-sm font-medium">{detail.value}</span>
                          </Label>
                          <Switch
                            id={`social-${detail.key}`}
                            className="shrink-0"
                            checked={Boolean(visibility[detail.key])}
                            onCheckedChange={(checked) => {
                              setVisibility((current) => ({ ...current, [detail.key]: checked }));
                              resetResults();
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">Add names and a date to the event before generating.</div>
                  )}
                </section>

                {generationError && (
                  <div className="rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">{generationError}</div>
                )}
                <AIPremiumButton
                  featureName="Social Studio design generation"
                  type="button"
                  className="w-full"
                  disabled={generating || varying || uploading || uploadingInspiration || !photoSelectionIsValid || !catalog.ai_ready}
                  onClick={generate}
                >
                  {generating ? 'Generating AI design…' : 'Generate image'}
                </AIPremiumButton>
              </CardContent>
            </Card>
          </div>

          <div className="min-w-0 max-w-full xl:sticky xl:top-5 xl:self-start">
            <Card className="min-w-0 max-w-full overflow-hidden">
              <CardHeader className="flex-row items-center justify-between space-y-0 p-4 pb-3">
                <CardTitle className="text-base sm:text-lg">Generated design</CardTitle>
                {(generating || varying) ? (
                  <Badge variant="secondary">AI at work</Badge>
                ) : selectedDesign && (
                  <Badge variant="secondary">Design {selectedDesignIndex + 1}</Badge>
                )}
              </CardHeader>
              <CardContent className="min-w-0 max-w-full space-y-3 overflow-hidden p-4 pt-0">
                <div
                  className="relative flex min-h-[320px] w-full min-w-0 max-w-full touch-pan-y select-none items-center justify-center overflow-hidden rounded-xl bg-muted/50 p-2 sm:min-h-[440px] sm:p-3 xl:min-h-[520px] xl:p-4"
                  role="group"
                  aria-label="Generated design preview. Use the arrow keys or swipe to change direction."
                  tabIndex={selectedDesign ? 0 : -1}
                  onKeyDown={handlePreviewKeyDown}
                  onTouchStart={(event) => {
                    swipeStartXRef.current = event.touches[0]?.clientX ?? null;
                  }}
                  onTouchEnd={handlePreviewTouchEnd}
                  onTouchCancel={() => { swipeStartXRef.current = null; }}
                >
                  {(generating || varying) ? (
                    <PreviewGenerationLoader stage={generationStage} isVariation={varying} />
                  ) : generationError ? (
                    <PreviewGuidance
                      message={generationError}
                      onRetry={retryAction === 'variation' ? createVariation : generate}
                      retryDisabled={
                        !photoSelectionIsValid
                        || uploadingInspiration
                        || !catalog.ai_ready
                        || (retryAction === 'variation' && variationInstruction.trim().length < 3)
                      }
                      retryLabel={retryAction === 'variation' ? 'Try variation again' : 'Try generation again'}
                    />
                  ) : selectedDesign ? (
                    <DesignCanvas
                      design={selectedDesign}
                      maximumHeight={680}
                      className="shrink rounded-lg"
                    />
                  ) : (
                    <div className="max-w-xs text-center text-sm text-muted-foreground">
                      Your generated design will appear here after all required event details and a couple photo are available.
                    </div>
                  )}
                  {selectedDesign && designs.length > 1 && !generating && !varying && !generationError && (
                    <>
                      <Button
                        type="button"
                        variant="secondary"
                        size="icon"
                        className="absolute left-2 top-1/2 z-20 -translate-y-1/2 rounded-full bg-background/90 shadow-lg backdrop-blur hover:bg-background"
                        aria-label="Previous design"
                        onClick={() => selectAdjacentDesign(-1)}
                      >
                        <CaretLeft size={20} weight="bold" />
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="icon"
                        className="absolute right-2 top-1/2 z-20 -translate-y-1/2 rounded-full bg-background/90 shadow-lg backdrop-blur hover:bg-background"
                        aria-label="Next design"
                        onClick={() => selectAdjacentDesign(1)}
                      >
                        <CaretRight size={20} weight="bold" />
                      </Button>
                    </>
                  )}
                </div>

                {designs.length > 0 && (
                  <div className="min-w-0 max-w-full space-y-2 overflow-hidden border-t pt-4">
                    <div className="flex items-center justify-between gap-3">
                      <Label>Generated images</Label>
                      <span className="text-xs text-muted-foreground">
                        {selectedDesignIndex + 1} of {designs.length}
                      </span>
                    </div>
                    <div className="grid max-w-full auto-cols-[76px] grid-flow-col gap-2 overflow-x-auto pb-2">
                      {designs.map((design, index) => (
                        <button
                          key={design.id}
                          type="button"
                          aria-label={`Show design ${index + 1}`}
                          aria-pressed={selectedDesign?.id === design.id}
                          title={design.creative_direction}
                          onClick={() => selectDesign(design)}
                          className={`snap-start rounded-lg border p-1.5 text-center transition ${selectedDesign?.id === design.id ? 'border-wedding-gold bg-wedding-gold/10 ring-2 ring-wedding-gold/30' : 'hover:border-wedding-sage'}`}
                        >
                          <DesignCanvas design={design} className="rounded-md" />
                          <span className="mt-1 block text-[0.625rem] font-semibold uppercase tracking-wide text-muted-foreground">
                            {index + 1}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {selectedDesign && !generating && !varying && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <Button variant="outline" onClick={share}>
                        <ShareNetwork size={17} className="mr-2" /> Share
                      </Button>
                      <Button variant="outline" onClick={download}>
                        <DownloadSimple size={17} className="mr-2" /> Download PNG
                      </Button>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full text-destructive hover:bg-destructive/10 hover:text-destructive"
                      disabled={removingDesignId === selectedDesign.id}
                      onClick={removeDesign}
                    >
                      <Trash size={17} className="mr-2" />
                      {removingDesignId === selectedDesign.id ? 'Removing design...' : 'Remove design from event'}
                    </Button>
                    <div className="space-y-2 rounded-xl border p-4">
                      <Label htmlFor="variation-instruction">Refine this AI design</Label>
                      <Textarea
                        id="variation-instruction"
                        value={variationInstruction}
                        maxLength={280}
                        rows={3}
                        onChange={(event) => setVariationInstruction(event.target.value)}
                      />
                      <div className="grid gap-2 sm:grid-cols-2">
                        <AIPremiumButton
                          featureName="Social Studio design generation"
                          type="button"
                          variant="outline"
                          className="w-full"
                          disabled={generating || varying || uploading || uploadingInspiration || !photoSelectionIsValid || !catalog.ai_ready}
                          onClick={generate}
                        >
                          <Sparkle size={17} className="mr-2" /> Generate fresh image
                        </AIPremiumButton>
                        <AIPremiumButton
                          featureName="Social Studio AI variations"
                          type="button"
                          variant="secondary"
                          className="w-full"
                          disabled={varying || variationInstruction.trim().length < 3}
                          onClick={createVariation}
                        >
                          <ArrowClockwise size={17} className="mr-2" />
                          {varying ? 'Refining design…' : 'Refine design'}
                        </AIPremiumButton>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
